import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import { ErrorBoundary } from "@sentry/react";
import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { AnalysisFilterField, exportDashboardAnalysis, getDashboardAnalysisFilters } from "../../api/analysisApi";
import { ErrorMsg } from "../../components/AppMessages";
import { CedarShellLayout } from "../../components/Layouts";
import { ALL_OPTION_NO_SPACE } from "../../constants";
import { useDashboardRegistry } from "../../hooks/react-query/dashboards/useDashboardRegistry";
import { ExportDownloadOut, isExportDownload, triggerExportDownload } from "../../utils/downloadExport";
import {
  ApplyFiltersTile,
  BRAND_PERFORMANCE_FISCAL_YEAR_PAGES,
  CustomRangeControl,
  customRangeControl,
  normalizeOption,
  toOptionsArray,
} from "./components/ApplyFiltersTile";
import { periodLabel } from "./components/FiscalYearFilterField";
import { SelectDashboardTile } from "./components/SelectDashboardTile";
import "../../assets/styles/pages/analyticsPage.scss";

// A multiselect field defaults to "All" (converted to an empty list on submission - see
// buildRequestFilters below), same convention as every other pre-existing dashboard's own
// market/region-style filters (e.g. VocQuickAfterSalesFilters.tsx/quickVocSalesApi.ts's own
// `market == "All" ? [] : ...`) - not whatever single real value the backend's own `default`
// happens to be (that's a byproduct of the field being a required List[...] with one seed value,
// not a deliberate "start the user here" choice).
// "fiscal_year" is a synthetic, display-only field (FiscalYearFilterField.tsx/services/analysis/
// service.py's own comment) - not a real submittable filter key at all (its own `default` is
// always null, never a real value; the picked periods are submitted as `date_range` instead - see
// buildRequestFilters). Including it here would send a stray `fiscal_year: null` in every request
// for a page that has one, alongside whatever `date_range` label was actually picked.
const defaultFilterValues = (fields: AnalysisFilterField[]): Record<string, unknown> =>
  Object.fromEntries(
    fields
      .filter((field) => field.type !== "fiscal_year")
      .filter((field) => field.type === "multiselect" || field.default !== undefined)
      .map((field) => [field.key, field.type === "multiselect" ? ALL_OPTION_NO_SPACE : field.default])
  );

// Sent on a dashboard's very first filters fetch (before the user has picked anything) instead of
// an empty {} - date_range is required with no schema-level default on at least one dashboard
// (DIS), so the very first call needs a real value here regardless of which dashboard it's for.
// `brand` deliberately isn't included here: its presence and shape both differ per dashboard
// (DIS's own schema is `Optional[List[str]] = ["Nissan"]` - optional, a list, already defaulted -
// but Ecommerce's is a required, non-list InternalBrand enum member) - there's no one hardcoded
// presence/shape safe for every dashboard, so this leaves `brand` out and lets the adaptive retry
// below correct for whichever dashboard actually needs it, rather than guessing up front.
const INITIAL_FILTER_VALUES: Record<string, unknown> = { date_range: "last_quarter" };

const BRAND_KEY = "brand";
// A value valid across every dashboard's shared brand enum - confirmed from a real 422's own
// ctx.enum_values (InternalBrand.Nissan/Infiniti/Datsun). Only the container shape and
// requiredness differ per dashboard, never this value itself.
const UNIVERSAL_BRAND_VALUE = "Nissan";
const MAX_FILTER_RETRIES = 2;

// Backend's 422 detail is FastAPI's own str(exc.errors()) rendering (a Python repr, not real
// JSON) - e.g. "Invalid filter selections: [{'loc': ('brand',), 'msg': 'field required', ...}]".
// This is a best-effort scan for each error entry's own `'loc': ('<field>'` fragment, not a real
// parse (there's no stable JSON contract for this string), just enough to tell which field(s) the
// backend is unhappy about.
const findInvalidFilterFields = (errorMessage: string): string[] =>
  Array.from(errorMessage.matchAll(/'loc':\s*\('([a-zA-Z0-9_]+)'/g)).map((match) => match[1]);

// Adapts brand's presence/shape based on what the backend's own error just said was wrong, rather
// than a per-dashboard table: absent entirely -> add it as a scalar first (Ecommerce's own shape);
// present but rejected again -> flip scalar<->array, covering a dashboard that turns out to want
// the opposite of whatever was just tried.
const adaptBrandShape = (values: Record<string, unknown>): Record<string, unknown> => {
  if (!(BRAND_KEY in values)) return { ...values, [BRAND_KEY]: UNIVERSAL_BRAND_VALUE };
  const current = values[BRAND_KEY];
  return { ...values, [BRAND_KEY]: Array.isArray(current) ? current[0] ?? UNIVERSAL_BRAND_VALUE : [current] };
};

// A field's real submission shape is derived from its own `type` in the filters response, not
// guessed or hardcoded per dashboard: "multiselect" is a backend List[...] field (confirmed
// against DIS's brand/market/lead_group/leads_source, all List[str]) - MultiSelectFilterField.tsx
// lets the user pick more than one, joined into one comma-separated string (matching how every
// value already travels through onChange/values as a plain string), so this splits it back into
// the real array a List[...] field expects. Anything else is a scalar enum/string/date field, sent
// as-is. Values with no matching field (e.g. the very first fetch, before any filters response
// exists yet to consult) pass through unchanged.
//
// "All" (the default - see defaultFilterValues above) is this UI's own sentinel, not a real
// backend value - it becomes an empty list, not this field's own full option list. Confirmed
// against every real dashboard's own equivalent params-builder (brandPerformanceApi.ts,
// cpoOperationalApi.ts, ccsCockpitApi.ts, activationRatioApi.ts, midasApi.ts, topFlopApi.ts,
// ccsAnalysisApi.ts - all `/all/gi.test(...) ? [] : value.split(",")` or the exact `=== "All" ? []
// : ...` equivalent) - every one of them sends `[]` for "All", never the expanded value list, and
// the backend's own shared `evaluate_All_to_None` validator only special-cases a literal "All"
// *string*, never an empty list, so `[]` reaches the same "no constraint" backend code path a real
// dashboard's own default (also effectively "All" on first load) already relies on. A previous
// version of this comment claimed the opposite ("every other dashboard... sends every real
// value") - that wasn't actually checked against this codebase's own dashboards at the time; this
// is the corrected, verified behavior.
//
// date_range gets the same array-wrapping treatment as a "multiselect" field, but only when its
// own value looks like a FiscalYearFilterField.tsx label ("Q1 FY 2026"/"April FY 2026", ...,
// comma-joined for more than one) - matching the real Brand Performance dashboard's own
// date_range_brand_perf handling (brandPerformanceApiParamsToObject's own
// `parameters?.get("date_range_brand_perf")?.split(",")`), which always sends this as an array,
// never a bare string, even for a single label. Every other dashboard's own date_range values
// ("last_quarter", "custom", ...) never match this shape, so this is scoped tightly to that one
// field/format rather than touching date_range's normal scalar handling everywhere else.
const FISCAL_YEAR_LABEL = /\bFY \d/;
// start_date/end_date are only meaningful once date_range itself is null/unset, or explicitly the
// "custom" sentinel (ApplyFiltersTile.tsx's own showDateFields uses the same "custom" check to
// decide whether to render the date pickers at all) - every other real date_range value (a preset
// like "last_quarter", or a fiscal-year label) already fully determines the range on its own, so
// start_date/end_date have nothing left to add and are dropped from the outgoing payload entirely
// rather than sent alongside it as null.
const isMeaninglessDateBound = (key: string, values: Record<string, unknown>): boolean =>
  (key === "start_date" || key === "end_date") &&
  values.date_range !== null &&
  values.date_range !== undefined &&
  values.date_range !== "custom";
// Brand Performance's own category/sub_category/data_category filters are always a real list on
// the actual dashboard (brandPerformanceApi.ts's own unconditional `.split(",")` for each,
// confirmed directly) - reported bug: Brand Health Index's own `category` was reaching the backend
// as a bare string instead. Checked independently of the response's own reported field `type`
// (unlike the generic "multiselect" handling below) since that's been unreliable before (CPO
// Operational's `year` field's options, the "All" semantics fix) and there's no way to re-verify
// it against a live backend from here - these three keys are specific enough to this one dashboard
// family that treating them as always-array is safe even if `type` turns out fine after all.
const ALWAYS_ARRAY_FILTER_KEYS = new Set(["category", "sub_category", "data_category"]);
// Use Ownership Overview / Online Account and Engagement / Service Lead / Self Service's own
// dashboard pages never actually send "market" as [] for "All" - UseOwnershipFilters.tsx's own
// param-correcting effect (see its "Validate market param" comment) always resolves the market URL
// param to the full comma-joined market list, even when nothing specific is selected, so
// apiParamsToObjectUseOwnership's `market == "All" ? [] : ...` branch never actually fires on the
// real dashboard - only this Downloads page's own generic "All" handling below does, producing a
// [] this family's backend endpoints apparently treat differently from the explicit full list
// (reported: CSVs differ between the two pages). Scoped to just these four dashboard keys and the
// "market" field specifically - every other dashboard's own equivalent params-builder really does
// send [] for "All" (see this function's own comment below) and isn't affected.
//
// Keys are the real GET /dashboards ones (confirmed against a live response), NOT
// constants/dashboardRegistry.ts's - that file is an explicitly-labeled mock of the planned seed,
// predating the real one landing, and its "poi_use_ownership_..." naming never matched what was
// actually seeded (real keys have no such prefix at all). The first attempt at this fix used the
// mock's keys and silently matched nothing.
const FULL_MARKET_LIST_DASHBOARD_IDS = new Set(["overview", "online_account_and_engagement", "service_lead", "self_service"]);
// A fiscal-year page's Fiscal Year picker writes its periods to `fiscal_year` while the date_range
// dropdown stays on "custom" (ApplyFiltersTile.tsx) - "custom" plus picked periods is submitted as
// those periods' labels in date_range, the same array the picker's own direct date_range write
// produces below. `fiscal_year` itself is never a real backend filter, so it's never sent.
const pickedFiscalYearPeriods = (values: Record<string, unknown>): string[] =>
  typeof values.fiscal_year === "string" && values.fiscal_year ? values.fiscal_year.split(",") : [];
// "custom" without its periods/dates filled in yet isn't a range the backend can compute options
// against - the page's options lookup raises ("start_date and end_date are required for a custom
// date_range"), and /filters comes back with no options for any other field. Left out of /filters
// requests until it's complete, so the backend computes options against the page's own default
// range; the dropdown itself stays on "custom".
const CUSTOM_RANGE_KEYS = new Set(["date_range", "fiscal_year", "start_date", "end_date"]);
const omitKeys = (values: Record<string, unknown>, keys: Set<string>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(values).filter(([key]) => !keys.has(key)));
const withoutIncompleteCustomRange = (values: Record<string, unknown>, control: CustomRangeControl): Record<string, unknown> => {
  if (values.date_range !== "custom") return values;
  if (control === "fiscal_year") {
    return pickedFiscalYearPeriods(values).length > 0 ? values : omitKeys(values, new Set(["date_range"]));
  }
  return values.start_date && values.end_date ? values : omitKeys(values, new Set(["date_range", "start_date", "end_date"]));
};
// Why a "custom" range can't be applied yet, or null when it can. Download CSV only appears once
// Apply succeeds, and an export with "custom" but no periods/dates fails backend-side, so Apply
// stops here with this message instead. How "custom" is filled in comes from customRangeControl,
// falling back to whichever of fiscal_year/start_date/end_date this page's response has.
const incompleteCustomRangeMessage = (
  dashboardId: string,
  page: string | undefined,
  fields: AnalysisFilterField[],
  values: Record<string, unknown>
): string | null => {
  if (values.date_range !== "custom") return null;
  const control =
    customRangeControl(dashboardId, page) ??
    (fields.some((field) => field.type === "fiscal_year")
      ? "fiscal_year"
      : fields.some((field) => field.key === "start_date" || field.key === "end_date")
      ? "dates"
      : undefined);
  if (control === "fiscal_year" && pickedFiscalYearPeriods(values).length === 0) {
    return "Select at least one fiscal period for a custom date range.";
  }
  if (control === "dates" && !(values.start_date && values.end_date)) {
    return "Select both a start and an end date for a custom date range.";
  }
  return null;
};
// The request for a page being switched TO carries no "custom" range at all - the previous page's
// periods/dates may not be how the new page fills "custom" (customRangeControl), and sending them
// anyway leaves the backend unable to compute options for every other field.
const withoutCustomRange = (values: Record<string, unknown>): Record<string, unknown> =>
  values.date_range === "custom" ? omitKeys(values, CUSTOM_RANGE_KEYS) : values;
const buildRequestFilters = (
  dashboardId: string,
  fields: AnalysisFilterField[],
  values: Record<string, unknown>
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(values)
      .filter(([key]) => key !== "fiscal_year" && !isMeaninglessDateBound(key, values))
      .map(([key, value]) => {
        if (key === "date_range" && value === "custom" && pickedFiscalYearPeriods(values).length > 0) {
          return [key, pickedFiscalYearPeriods(values)];
        }
        const field = fields.find((f) => f.key === key);
        const isListField = field?.type === "multiselect" || ALWAYS_ARRAY_FILTER_KEYS.has(key);
        if (isListField && value === ALL_OPTION_NO_SPACE && key === "market" && FULL_MARKET_LIST_DASHBOARD_IDS.has(dashboardId)) {
          const options = field?.options ?? [];
          return [key, options.map((option) => (typeof option === "object" ? option.value : String(option)))];
        }
        if (isListField && value === ALL_OPTION_NO_SPACE) {
          return [key, []];
        }
        if (isListField && typeof value === "string") {
          return [key, value.split(",")];
        }
        if (isListField && value !== undefined && value !== null && !Array.isArray(value)) {
          return [key, [value]];
        }
        if (key === "date_range" && typeof value === "string" && FISCAL_YEAR_LABEL.test(value)) {
          return [key, value.split(",")];
        }
        return [key, value];
      })
  );

// A dashboard with more than one named export 400s its own real option list directly in the error
// message when `export` is omitted - e.g. "Query param 'export' is required for this dashboard
// page. Options: ['channel_mix', 'overall']". Parses that Python-repr-style list (not real JSON,
// same reasoning as findInvalidFilterFields above) rather than a hardcoded per-dashboard table,
// which already drifted out of sync with a real backend registry change once (analysisApi.ts's own
// comment on exportDashboardAnalysis).
const findExportOptions = (errorMessage: string): string[] => {
  const match = errorMessage.match(/Options:\s*\[(.*)\]/);
  if (!match) return [];
  return Array.from(match[1].matchAll(/'([^']*)'/g)).map((entry) => entry[1]);
};

// Distinct from a validation 422 - this is the backend's own plain-string 400 detail (not a
// {loc, msg, type} array) for a dashboard registered with more than one named "page" (services/
// analysis/service.py's _resolve_capability, backend) when none was supplied - e.g. "Query param
// 'page' is required for this dashboard." Unlike the export-name 400 above, this message never
// includes the valid options list, so there's nothing to auto-discover beyond "this dashboard
// needs one" - the real value has to come from the user (ApplyFiltersTile.tsx's own page input).
// No hardcoded per-dashboard list of which ones need this either - discovered the same adaptive
// way as brand's shape/export names, since a hand-maintained list is exactly what's already
// drifted out of sync with a real backend change once (see exportDashboardAnalysis's own comment).
const isPageRequiredError = (errorMessage: string): boolean => /query param ['"]?page['"]? is required/i.test(errorMessage);

export const AnalyticsPage = () => {
  const location = useLocation();
  const { dashboards: allDashboards } = useDashboardRegistry();
  // Live on the backend (docs/needed-endpoints.md §6) via a real generic /analysis/{dashboard_id}
  // endpoint, but only for dashboards actually registered there - `supports_export` is this
  // page's own signal for which ones that is, so the picker only ever offers a dashboard the real
  // endpoint can actually answer for. is_active now doubles as "archived" (dashboardsApi.ts's own
  // getDashboardRegistry comment) - export stays blocked for an archived dashboard, so it's
  // excluded here too rather than letting it appear as a pickable option every export attempt
  // would then fail against.
  const dashboards = allDashboards.filter((dashboard) => dashboard.supports_export && dashboard.is_active);

  const [selectedDashboardIds, setSelectedDashboardIds] = useState<string[]>([]);
  const [filterFieldsByDashboard, setFilterFieldsByDashboard] = useState<Record<string, AnalysisFilterField[]>>({});
  const [filterValuesByDashboard, setFilterValuesByDashboard] = useState<Record<string, Record<string, unknown>>>({});
  // The exact values last confirmed via a successful Apply - a distinct snapshot from
  // filterValuesByDashboard (which keeps changing live as the user edits dropdowns), since Export
  // must send what the backend actually validated, not whatever's currently sitting unconfirmed in
  // the form. Captured in handleApplyFilters below, using fetchFilters' own resolved values (not
  // the pre-retry input) since the brand adaptive-retry may have changed what was actually sent.
  const [appliedFilterValuesByDashboard, setAppliedFilterValuesByDashboard] = useState<Record<string, Record<string, unknown>>>({});
  // True once Apply Filters has succeeded at least once for that dashboard, and reset back to
  // false the moment any filter changes afterward (see handleFilterChange) - Download CSV has
  // nothing confirmed-valid to export against until Apply succeeds again.
  const [filtersAppliedByDashboard, setFiltersAppliedByDashboard] = useState<Record<string, boolean>>({});
  // Only meaningful for a dashboard with more than one named export - omitted from the request
  // entirely for every other dashboard. Set once the user picks one, or automatically once
  // exportOptionsByDashboard below is discovered (see handleDownloadCsv).
  const [exportNameByDashboard, setExportNameByDashboard] = useState<Record<string, string>>({});
  // A dashboard's real export names, discovered on demand from a 400's own error message (see
  // findExportOptions/handleDownloadCsv) rather than a hardcoded per-dashboard table - empty until
  // the first Download CSV attempt reveals them (or never populated at all for a single-export
  // dashboard, which needs no `export` param in the first place).
  const [exportOptionsByDashboard, setExportOptionsByDashboard] = useState<Record<string, Array<{ value: string; label: string }>>>({});
  // A dashboard's own currently-selected page (only meaningful for a dashboard registered with
  // more than one named page, e.g. brand_performance's brand_volume/sov_som/purchase_funnel) -
  // empty for every other dashboard, which never needs this param in the first place. Set either
  // from the backend's own chosen default (see fetchFilters' own pageOptions handling below) or by
  // the user picking a different one.
  const [pageByDashboard, setPageByDashboard] = useState<Record<string, string>>({});
  // A dashboard's real page names, already resolved server-side - populated the same way
  // exportOptionsByDashboard below is, so the Page selector appears proactively (letting the user
  // choose which page to export from) rather than only after a 400 (see needsPageByDashboard's own
  // comment for that older, now-fallback-only path).
  const [pageOptionsByDashboard, setPageOptionsByDashboard] = useState<Record<string, Array<{ value: string; label: string }>>>({});
  // True once this dashboard's filters/export calls have told us they need an explicit page value
  // (see isPageRequiredError) - discovered adaptively, not assumed up front for any dashboard. Kept
  // as a defensive fallback (a free-text page input - ApplyFiltersTile.tsx) for the rare response
  // that doesn't carry pageOptions above; the backend's own /filters endpoint no longer actually
  // 400s for a missing page (it defaults to the first registered one instead), so this path is
  // expected to go mostly unused now.
  const [needsPageByDashboard, setNeedsPageByDashboard] = useState<Record<string, boolean>>({});
  // True while a fetchFilters call (including its own internal brand-shape retries) is still in
  // flight for this dashboard - covers exactly the window where filterFieldsByDashboard[dashboardId]
  // is undefined for a reason other than "this dashboard genuinely has none" (ApplyFiltersTile.tsx's
  // own isLoading check). Set around each of the three outer call sites below, not inside
  // fetchFilters itself, so an internal retry doesn't flip this false/true again mid-flight.
  const [filtersLoadingByDashboard, setFiltersLoadingByDashboard] = useState<Record<string, boolean>>({});
  // True once this dashboard's /filters request has come back 403 - a coarser "can you see this
  // dashboard at all" check (SelectDashboardTile.tsx only offers supports_export dashboards) is
  // not the same as this finer-grained "can you see THIS dashboard's actual data" one, so a
  // selectable dashboard can still 403 here. Distinct from the generic retry/toast path below so
  // the tile can show what's actually true instead of a generic "please try again" that implies
  // retrying would help.
  const [filtersAccessDeniedByDashboard, setFiltersAccessDeniedByDashboard] = useState<Record<string, boolean>>({});
  // True from the moment a dashboard's Download CSV is clicked until its export request settles
  // (success or failure) - previously there was no feedback at all while an export was in flight,
  // just a button that looked exactly the same whether it had been clicked or not. Stays true
  // across handleDownloadCsv's own internal retry (the self-correcting "discover the real export
  // name from a 400" pass) - only ever cleared in that function's two terminal branches (a
  // real download, or the final toast.error), never in the retry branch itself.
  const [downloadingByDashboard, setDownloadingByDashboard] = useState<Record<string, boolean>>({});
  // Why the last Apply Filters click was refused for this dashboard (incompleteCustomRangeMessage)
  // - shown in its tile until the selections change.
  const [applyErrorByDashboard, setApplyErrorByDashboard] = useState<Record<string, string | undefined>>({});
  const clearApplyError = (dashboardId: string) =>
    setApplyErrorByDashboard((prev) => (prev[dashboardId] ? { ...prev, [dashboardId]: undefined } : prev));
  // Per-dashboard debounce timer for handleFilterChange's own auto-refetch below - a ref (not
  // state) since it's write-only bookkeeping that should never itself trigger a re-render.
  const filterChangeDebounceRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  // Clears every dashboard's pending debounce timer on unmount - without this, one scheduled just
  // before the page unmounts (e.g. navigating away, or - the way this actually surfaced - a test
  // finishing) still fires later against a real timer clock, calling fetchFilters (and therefore
  // the real getDashboardAnalysisFilters API) for a component that's no longer there to use the
  // result, polluting whatever runs next in the same process (a later test's own assertions on
  // that same mocked/spied function, in particular).
  useEffect(
    () => () => {
      Object.values(filterChangeDebounceRef.current).forEach(clearTimeout);
    },
    []
  );

  // Resolves with the exact values that succeeded (not just a boolean), plus the filter fields
  // themselves - callers need both: the values for "the applied values" snapshot
  // (handleApplyFilters), and the fields to tell whether this dashboard has any configurable
  // filters at all (markAppliedIfNoFilters below). Resolves null on failure (exhausted retries, or
  // this dashboard needing a page value that hasn't been supplied yet).
  const fetchFilters = (
    dashboardId: string,
    numericId: number,
    values: Record<string, unknown>,
    attempt = 0,
    // Explicit override for the page a caller is switching TO (handlePageChange below) - falls
    // back to whatever's already selected otherwise. Threaded through the brand-shape retry
    // recursion too, so a retry doesn't silently drop back to some other page mid-attempt.
    pageOverride?: string
  ): Promise<{ values: Record<string, unknown>; filters: AnalysisFilterField[] } | null> => {
    const page = pageOverride ?? pageByDashboard[dashboardId] ?? undefined;
    const isPageSwitch = pageOverride !== undefined && pageOverride !== pageByDashboard[dashboardId];
    const control = customRangeControl(dashboardId, page);
    const requestValues = isPageSwitch ? withoutCustomRange(values) : control ? withoutIncompleteCustomRange(values, control) : values;
    return getDashboardAnalysisFilters(
      numericId,
      buildRequestFilters(dashboardId, filterFieldsByDashboard[dashboardId] ?? [], requestValues),
      {
        page: page || undefined,
      }
    ).then((response) => {
      if (!response || "error" in response) {
        const errorMessage = typeof (response as { error?: unknown })?.error === "string" ? (response as { error: string }).error : "";
        if (isPageRequiredError(errorMessage)) {
          // Not a transient failure - an expected, one-time state for a multi-page dashboard until
          // the user types a page value (ApplyFiltersTile.tsx's own page input) and applies again.
          // No generic "couldn't load" toast for this one; that would misrepresent a normal,
          // resolvable state as an error. Kept as a fallback - the backend's own /filters endpoint
          // no longer 400s this way in practice (it defaults to the first registered page instead
          // and returns pageOptions below), but this stays in case some response doesn't carry that.
          setNeedsPageByDashboard((prev) => ({ ...prev, [dashboardId]: true }));
          return null;
        }
        if ((response as { status?: number })?.status === 403) {
          // Not the same as "couldn't load, try again" - retrying won't help until access is
          // actually granted, so this gets its own inline message (ApplyFiltersTile.tsx) instead
          // of the generic toast/retry path below.
          setFiltersAccessDeniedByDashboard((prev) => ({ ...prev, [dashboardId]: true }));
          return null;
        }
        const invalidFields = findInvalidFilterFields(errorMessage);
        if (attempt < MAX_FILTER_RETRIES && invalidFields.includes(BRAND_KEY)) {
          return fetchFilters(dashboardId, numericId, adaptBrandShape(values), attempt + 1, page);
        }
        toast.error("Couldn't load filters for this dashboard - please try again.");
        return null;
      }
      // A dashboard that previously needed a page (or still has the flag set from an earlier
      // failed attempt) just succeeded - whatever page value is currently set is confirmed valid.
      setNeedsPageByDashboard((prev) => (prev[dashboardId] ? { ...prev, [dashboardId]: false } : prev));
      // Same for a dashboard that previously 403'd (e.g. access was granted since) - a successful
      // fetch here means it's no longer denied.
      setFiltersAccessDeniedByDashboard((prev) => (prev[dashboardId] ? { ...prev, [dashboardId]: false } : prev));
      // The real page list, already resolved server-side - populated here so the Page selector
      // (letting the user choose which page to export the CSV from, e.g. brand_performance's own
      // brand_volume/sov_som/purchase_funnel/...) appears proactively for every request, not only
      // after a 400 (needsPageByDashboard's own fallback above). response.page is the backend's
      // own chosen default (the first registered page) whenever none was specified - only actually
      // set here if this dashboard doesn't already have its own selection (a user-picked page
      // shouldn't get silently reset back to the default by an unrelated filter-change refetch).
      if (Array.isArray(response.pages) && response.pages.length > 1) {
        const pages = response.pages;
        setPageOptionsByDashboard((prev) => ({
          ...prev,
          [dashboardId]: pages.map((pageDescriptor) => ({ value: pageDescriptor.key, label: pageDescriptor.label })),
        }));
        if (!pageByDashboard[dashboardId] && response.page) {
          setPageByDashboard((prev) => ({ ...prev, [dashboardId]: response.page as string }));
        }
      }
      // `filters` isn't a fixed schema (AnalysisFilterField's own comment) - a real dashboard has
      // been seen returning a non-array value for a field's own `options`, so the top-level list
      // itself isn't assumed safe either; ApplyFiltersTile.tsx's own render guards each field's
      // `options` the same way.
      const effectivePage = page || (typeof response.page === "string" ? response.page : undefined);
      const safeFilters = (Array.isArray(response.filters) ? response.filters : []).filter(
        (filter) =>
          filter.type !== "fiscal_year" ||
          dashboardId !== "brand_performance" ||
          (effectivePage !== undefined && BRAND_PERFORMANCE_FISCAL_YEAR_PAGES.has(effectivePage))
      );
      setFilterFieldsByDashboard((prev) => ({ ...prev, [dashboardId]: safeFilters }));
      // The real export list, already resolved server-side - populated here (once there's
      // genuinely more than one to choose from, matching exportOptionsByDashboard's own established
      // contract below) so the Export selector appears before the first Download click, not only
      // after it 400s. The reactive discovery in handleDownloadCsv's own catch branch stays as a
      // defensive fallback for the rare response that doesn't carry this.
      if (Array.isArray(response.exports) && response.exports.length > 1) {
        const exports = response.exports;
        setExportOptionsByDashboard((prev) => ({
          ...prev,
          [dashboardId]: exports.map((exportDescriptor) => ({ value: exportDescriptor.key, label: exportDescriptor.label })),
        }));
      }
      // granularity's own accepted options can narrow between pages of the SAME dashboard
      // (brand_performance's own granularity is "quarterly"/"yearly" only for pages like
      // brand_oao/nim, but the full daily/weekly/monthly/quarterly/yearly enum for others like
      // brand_volume - services/analysis/describe.py's own per-page enum narrowing, backend).
      // handlePageChange's own refetch doesn't clear this dashboard's already-chosen granularity
      // value, so a value that was valid on the PREVIOUS page (e.g. "monthly") silently rides
      // along into the new page's request and 422s ("granularity must be either quarterly or
      // yearly"). Dropped back to this field's own fresh default below instead, whenever it's no
      // longer one of the new response's own accepted values. Scoped to granularity specifically,
      // not every field with an `options` list - date_range's own options never include a picked
      // Fiscal Year label at all (that's a separately-synthesized value, see FISCAL_YEAR_LABEL
      // above), so checking it the same way would wrongly strip a valid fiscal-year selection back
      // to its default on every refetch.
      const granularityField = safeFilters.find((filter) => filter.key === "granularity");
      const acceptedGranularities = toOptionsArray(granularityField?.options)
        .map(normalizeOption)
        .map((option) => option.value);
      // A value for a field this response doesn't have is left over from a previous page (e.g.
      // brand_image's `attribute`, hidden on brand_volume) - kept, it would be sent with no field
      // left to say it's a list ("All" instead of [], which the backend 422s). Dropped, except the
      // date range keys, which can legitimately be absent from a response (CUSTOM_RANGE_KEYS).
      const responseKeys = new Set(safeFilters.map((filter) => filter.key));
      const validatedValues =
        safeFilters.length > 0
          ? Object.fromEntries(Object.entries(values).filter(([key]) => responseKeys.has(key) || CUSTOM_RANGE_KEYS.has(key)))
          : { ...values };
      if (granularityField && acceptedGranularities.length > 0 && !acceptedGranularities.includes(String(validatedValues.granularity))) {
        delete validatedValues.granularity;
      }
      // date_range's preset options differ per dashboard/page too (brand_oao offers only
      // last_6_quarters/custom - services/analysis/date_ranges.py, backend), so a preset picked
      // elsewhere (or INITIAL_FILTER_VALUES' own "last_quarter") can stop being offered. Moved to
      // the first offered option rather than left stale. A Fiscal Year label written straight to
      // date_range (a page whose picker has no date_range dropdown beside it) is never among these
      // options, so it's left alone.
      const dateRangeField = safeFilters.find((filter) => filter.key === "date_range");
      const acceptedDateRanges = toOptionsArray(dateRangeField?.options)
        .map(normalizeOption)
        .map((option) => option.value);
      const dateRange = validatedValues.date_range;
      if (
        dateRangeField &&
        acceptedDateRanges.length > 0 &&
        dateRange !== undefined &&
        dateRange !== null &&
        !(typeof dateRange === "string" && FISCAL_YEAR_LABEL.test(dateRange)) &&
        !acceptedDateRanges.includes(String(dateRange))
      ) {
        validatedValues.date_range = acceptedDateRanges[0];
      }
      // A "custom" range filled in one way (fiscal periods, or a start/end pair) can't carry onto a
      // page that fills it the other way (customRangeControl) - moved to the first offered option
      // instead, dropping whatever periods/dates the previous page had picked.
      if (
        isPageSwitch &&
        validatedValues.date_range === "custom" &&
        acceptedDateRanges.length > 0 &&
        customRangeControl(dashboardId, pageByDashboard[dashboardId]) !== control
      ) {
        validatedValues.date_range = acceptedDateRanges[0];
        delete validatedValues.fiscal_year;
        delete validatedValues.start_date;
        delete validatedValues.end_date;
      }
      // Picked fiscal periods are page-scoped too - keeps only those the new response still offers.
      const fiscalYearField = safeFilters.find((filter) => filter.type === "fiscal_year");
      if (pickedFiscalYearPeriods(validatedValues).length > 0) {
        const offeredPeriods = new Set(
          (Array.isArray(fiscalYearField?.options) ? (fiscalYearField?.options as any[]) : []).flatMap(
            (option: { fiscal_year: number; quarters?: string[]; months?: string[] }) =>
              (option.months ?? option.quarters ?? []).map((period) => periodLabel(period, option.fiscal_year))
          )
        );
        validatedValues.fiscal_year = pickedFiscalYearPeriods(validatedValues)
          .filter((period) => offeredPeriods.has(period))
          .join(",");
      }
      // Merges in every OTHER field's own default too, not just whichever keys `values` already
      // had (the two bootstrapped in INITIAL_FILTER_VALUES/the brand retry, or whatever the user's
      // touched so far) - a dashboard can return many more fields (market/model/currency/...) than
      // the couple this flow already knows about, and one the user never clicked a dropdown for
      // would otherwise be silently omitted from every subsequent Apply/Export call entirely,
      // rather than sent as its own real default. `validatedValues` still wins over a field's own
      // default for anything already known/edited (and still valid).
      const completeValues = { ...defaultFilterValues(safeFilters), ...validatedValues };
      setFilterValuesByDashboard((prev) => ({ ...prev, [dashboardId]: completeValues }));
      return { values: completeValues, filters: safeFilters };
    });
  };

  // A dashboard with no configurable filters at all has nothing for Apply Filters to do (that
  // button is disabled entirely for this case - see ApplyFiltersTile.tsx) - so nothing will ever
  // click it to confirm anything. Marks it as already-applied the moment its (empty) filters
  // response comes back instead, so Download CSV is available immediately.
  const markAppliedIfNoFilters = (
    dashboardId: string,
    result: { values: Record<string, unknown>; filters: AnalysisFilterField[] } | null
  ) => {
    if (!result || result.filters.length > 0) return;
    setFiltersAppliedByDashboard((prev) => ({ ...prev, [dashboardId]: true }));
    setAppliedFilterValuesByDashboard((prev) => ({ ...prev, [dashboardId]: result.values }));
  };

  // Landing here from the Home search bar (?dashboardId=...) adds that dashboard to the selection
  // (rather than replacing it) and fetches its filters - Download CSV still requires an explicit
  // Apply Filters click. Deferred to an effect (rather than validated synchronously on mount)
  // since the registry now loads asynchronously - dashboards is empty on the very first render,
  // so validating the query param against it has to wait until data has actually arrived.
  const dashboardIdFromQuery = new URLSearchParams(location.search).get("dashboardId");
  useEffect(() => {
    if (!dashboardIdFromQuery) return;
    const dashboard = dashboards.find((d) => d.key === dashboardIdFromQuery);
    if (!dashboard) return;
    setSelectedDashboardIds((prevIds) => {
      if (prevIds.includes(dashboardIdFromQuery)) return prevIds;
      // fetchFilters itself records whatever values actually succeeded (see its own comment) -
      // no need to eagerly guess filterValuesByDashboard here too.
      setFiltersLoadingByDashboard((prev) => ({ ...prev, [dashboardIdFromQuery]: true }));
      fetchFilters(dashboardIdFromQuery, dashboard.id, INITIAL_FILTER_VALUES)
        .then((result) => markAppliedIfNoFilters(dashboardIdFromQuery, result))
        .finally(() => setFiltersLoadingByDashboard((prev) => ({ ...prev, [dashboardIdFromQuery]: false })));
      return [...prevIds, dashboardIdFromQuery];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardIdFromQuery, dashboards]);

  const selectedDashboards = selectedDashboardIds
    .map((dashboardId) => dashboards.find((dashboard) => dashboard.key === dashboardId))
    .filter((dashboard): dashboard is NonNullable<typeof dashboard> => Boolean(dashboard));

  // A dashboard the user can't access can never have applied filters (its own tile hides Apply
  // Filters entirely - see ApplyFiltersTile.tsx) or finish "loading" in any meaningful sense - it's
  // permanently settled, not pending. Excluded from both checks below the same way, so selecting
  // one alongside dashboards the user *can* access doesn't leave the bulk controls stuck forever
  // (allSelectedFiltersLoaded) or Download all CSVs permanently unreachable for the rest of the
  // selection (allFiltersApplied).
  const accessibleSelectedDashboards = selectedDashboards.filter((dashboard) => !filtersAccessDeniedByDashboard[dashboard.key]);
  // True as soon as there's at least one selected dashboard the user can actually act on - Apply
  // all filters has nothing valid to do at all once every selected dashboard is access-denied, so
  // SelectDashboardTile.tsx hides it entirely then rather than leaving an enabled button that's a
  // guaranteed no-op (handleApplyAllFilters below already skips denied dashboards on its own).
  const hasAccessibleSelectedDashboard = accessibleSelectedDashboards.length > 0;

  // Every selected (accessible) dashboard needs its OWN confirmed-applied filters before Download
  // all CSVs is offered - this used to be .some(...) (enabled as soon as ANY one dashboard had
  // applied filters), which let a user click it believing every dashboard would export while
  // dashboards that were only added/selected but never (re-)applied got silently skipped by
  // handleDownloadAllCsvs below, with no indication anything was left out. .length > 0 guards an
  // empty selection, where .every(...) alone would vacuously return true with nothing to export.
  const allFiltersApplied =
    accessibleSelectedDashboards.length > 0 && accessibleSelectedDashboards.every((dashboard) => filtersAppliedByDashboard[dashboard.key]);

  // filterFieldsByDashboard only ever gets a dashboard's key once its filters response actually
  // resolves (fetchFilters's own setFilterFieldsByDashboard call) - it's undefined both while
  // still loading AND if that fetch failed outright (a 404/error never sets it). Apply all filters
  // has nothing real to do yet for an accessible dashboard in either state; an access-denied one
  // never resolves this either, but that's not "still loading" - see accessibleSelectedDashboards.
  const allSelectedFiltersLoaded = accessibleSelectedDashboards.every((dashboard) => filterFieldsByDashboard[dashboard.key] !== undefined);

  const handleAddDashboard = (dashboardId: string) => {
    const dashboard = dashboards.find((d) => d.key === dashboardId);
    if (!dashboard) return;
    setSelectedDashboardIds((prevIds) => (prevIds.includes(dashboardId) ? prevIds : [...prevIds, dashboardId]));
    setFiltersLoadingByDashboard((prev) => ({ ...prev, [dashboardId]: true }));
    fetchFilters(dashboardId, dashboard.id, INITIAL_FILTER_VALUES)
      .then((result) => markAppliedIfNoFilters(dashboardId, result))
      .finally(() => setFiltersLoadingByDashboard((prev) => ({ ...prev, [dashboardId]: false })));
  };

  // Also drops the dashboard's filter/results state (rather than just unselecting it) so
  // re-adding it later starts fresh instead of resurrecting stale values.
  const handleRemoveDashboard = (dashboardId: string) => {
    setSelectedDashboardIds((prevIds) => prevIds.filter((id) => id !== dashboardId));
    setFilterFieldsByDashboard((prev) => {
      const next = { ...prev };
      delete next[dashboardId];
      return next;
    });
    setFilterValuesByDashboard((prev) => {
      const next = { ...prev };
      delete next[dashboardId];
      return next;
    });
    setAppliedFilterValuesByDashboard((prev) => {
      const next = { ...prev };
      delete next[dashboardId];
      return next;
    });
    setFiltersAppliedByDashboard((prev) => {
      const next = { ...prev };
      delete next[dashboardId];
      return next;
    });
    setExportNameByDashboard((prev) => {
      const next = { ...prev };
      delete next[dashboardId];
      return next;
    });
    setExportOptionsByDashboard((prev) => {
      const next = { ...prev };
      delete next[dashboardId];
      return next;
    });
    setPageByDashboard((prev) => {
      const next = { ...prev };
      delete next[dashboardId];
      return next;
    });
    setPageOptionsByDashboard((prev) => {
      const next = { ...prev };
      delete next[dashboardId];
      return next;
    });
    setNeedsPageByDashboard((prev) => {
      const next = { ...prev };
      delete next[dashboardId];
      return next;
    });
    setFiltersLoadingByDashboard((prev) => {
      const next = { ...prev };
      delete next[dashboardId];
      return next;
    });
    setFiltersAccessDeniedByDashboard((prev) => {
      const next = { ...prev };
      delete next[dashboardId];
      return next;
    });
    clearTimeout(filterChangeDebounceRef.current[dashboardId]);
    delete filterChangeDebounceRef.current[dashboardId];
  };

  const handlePageChange = (dashboardId: string, page: string) => {
    setPageByDashboard((prev) => ({ ...prev, [dashboardId]: page }));
    clearApplyError(dashboardId);

    // A different page can have an entirely different filter schema (e.g. brand_performance's
    // brand_volume vs sov_som) - refreshes the same way handleFilterChange's own auto-refetch
    // below does, sharing its debounce (the free-text fallback page input - ApplyFiltersTile.tsx's
    // needsPage case - fires this on every keystroke, so this still needs debouncing same as any
    // other field edit; the proactive page <select> only fires once per real selection either way).
    const dashboard = dashboards.find((d) => d.key === dashboardId);
    if (dashboard) {
      clearTimeout(filterChangeDebounceRef.current[dashboardId]);
      filterChangeDebounceRef.current[dashboardId] = setTimeout(() => {
        // granularity's own accepted values are page-scoped (brand_performance's own granularity
        // is "quarterly"/"yearly" only for pages like brand_oao/nim, but the full
        // daily/weekly/monthly/quarterly/yearly enum for others like brand_volume - services/
        // analysis/describe.py's own per-page enum narrowing, backend) - there's no way to know
        // here whether the OLD page's value is still valid for the new one, and sending it anyway
        // risks 422ing this exact request ("granularity must be either quarterly or yearly")
        // before fetchFilters' own response ever comes back to say so. Dropped rather than sent;
        // fetchFilters repopulates it from the new page's own fresh default once the response
        // arrives (see its own comment on this).
        const valuesForNewPage = { ...(filterValuesByDashboard[dashboardId] ?? INITIAL_FILTER_VALUES) };
        delete valuesForNewPage.granularity;
        fetchFilters(dashboardId, dashboard.id, valuesForNewPage, 0, page);
      }, 400);
    }
    // Same reasoning as handleFilterChange - a still-unconfirmed page change shouldn't leave
    // Download CSV offering to export against whatever was last applied under the OLD page.
    setFiltersAppliedByDashboard((prev) => (prev[dashboardId] ? { ...prev, [dashboardId]: false } : prev));
  };

  const handleFilterChange = (dashboardId: string, key: string, value: string) => {
    const dashboard = dashboards.find((d) => d.key === dashboardId);
    clearApplyError(dashboardId);

    setFilterValuesByDashboard((prevValues) => {
      const nextValues = { ...prevValues[dashboardId], [key]: value };

      // Auto-refresh this dashboard's OWN filter options as soon as one changes - e.g. picking a
      // market narrows model's own option list, same reactive behavior every normal dashboard's
      // filters already have (the backend already re-derives each field's options from whatever's
      // currently selected - fetchFilters' own comment) - rather than only refreshing once Apply
      // Filters is explicitly clicked. Debounced per dashboard so a fast run of edits (or typing
      // into a no-options text-input field) fires one request, not one per keystroke/click.
      // Deliberately doesn't touch filtersAppliedByDashboard/appliedFilterValuesByDashboard -
      // that's still only ever set by an explicit Apply Filters click (just below), so Download
      // CSV keeps requiring it exactly as before; only the options shown become reactive.
      if (dashboard) {
        clearTimeout(filterChangeDebounceRef.current[dashboardId]);
        filterChangeDebounceRef.current[dashboardId] = setTimeout(() => {
          fetchFilters(dashboardId, dashboard.id, nextValues);
        }, 400);
      }

      return { ...prevValues, [dashboardId]: nextValues };
    });
    // Edited since the last Apply - whatever was previously applied no longer reflects the form,
    // so Download CSV shouldn't offer to export it until Apply confirms the new selections.
    setFiltersAppliedByDashboard((prev) => (prev[dashboardId] ? { ...prev, [dashboardId]: false } : prev));
  };

  const handleResetFilters = (dashboardId: string) => {
    setFilterValuesByDashboard((prevValues) => ({
      ...prevValues,
      [dashboardId]: defaultFilterValues(filterFieldsByDashboard[dashboardId] ?? []),
    }));
    setFiltersAppliedByDashboard((prev) => ({ ...prev, [dashboardId]: false }));
    clearApplyError(dashboardId);
  };

  const handleApplyFilters = (dashboardId: string) => {
    const dashboard = dashboards.find((d) => d.key === dashboardId);
    if (!dashboard) return;
    const applyError = incompleteCustomRangeMessage(
      dashboardId,
      pageByDashboard[dashboardId],
      filterFieldsByDashboard[dashboardId] ?? [],
      filterValuesByDashboard[dashboardId] ?? {}
    );
    setApplyErrorByDashboard((prev) => ({ ...prev, [dashboardId]: applyError ?? undefined }));
    if (applyError) {
      setFiltersAppliedByDashboard((prev) => ({ ...prev, [dashboardId]: false }));
      return;
    }
    // Falls back to INITIAL_FILTER_VALUES, not {} - filterValuesByDashboard only gets populated on
    // a SUCCESSFUL fetch, so a dashboard whose very first attempt failed (e.g. it needed a page
    // value not yet supplied) would otherwise lose date_range's own required starting value the
    // moment the user retries Apply Filters with a page finally typed in.
    setFiltersLoadingByDashboard((prev) => ({ ...prev, [dashboardId]: true }));
    fetchFilters(dashboardId, dashboard.id, filterValuesByDashboard[dashboardId] ?? INITIAL_FILTER_VALUES)
      .then((result) => {
        setFiltersAppliedByDashboard((prev) => ({ ...prev, [dashboardId]: result !== null }));
        if (result !== null) {
          setAppliedFilterValuesByDashboard((prev) => ({ ...prev, [dashboardId]: result.values }));
        }
      })
      .finally(() => setFiltersLoadingByDashboard((prev) => ({ ...prev, [dashboardId]: false })));
  };

  const handleApplyAllFilters = () => {
    // Skips access-denied dashboards - there's nothing valid for Apply Filters to do for one (its
    // own tile doesn't even render the button - see ApplyFiltersTile.tsx), so firing the request
    // anyway would just be a guaranteed-403 round trip on every single click.
    accessibleSelectedDashboards.forEach((dashboard) => handleApplyFilters(dashboard.key));
  };

  // `overrideExportName`, when passed, guards a single self-correction attempt AND supplies the
  // just-discovered name directly: if the very first Download CSV click for a multi-export
  // dashboard omits `export` (nobody's picked one yet, since the options aren't known before this
  // point), the 400 reveals the real option list - this retries once with the first discovered
  // option rather than making the user click Download again with nothing to go on, and populates
  // exportOptionsByDashboard so the selector appears for next time. The retry passes that name as
  // `overrideExportName` explicitly rather than relying on exportNameByDashboard/
  // exportOptionsByDashboard state - the setters just called above haven't been applied to this
  // render's closure yet, so reading state here would still see the old (empty) values.
  // Browsers silently drop every automatic file download after the first if several are triggered
  // close together without an intervening user gesture (Chrome in particular does this with no
  // visible error) - this is exactly why "Download all CSVs" only ever produced one real file even
  // though every export request itself came back 200: all of them resolved around the same time
  // and called triggerExportDownload back-to-back. Each export's own request still fires
  // immediately/in parallel (unaffected, and already covered by the test just above) - only the
  // actual trigger (a simulated click navigating to the signed URL) is serialized through this
  // queue, spaced far enough apart that the browser treats each as its own separate, allowed
  // download rather than a blocked batch. A ref (not state) since it's write-only bookkeeping that
  // should never itself trigger a re-render.
  const downloadTriggerQueueRef = useRef<Promise<void>>(Promise.resolve());
  const DOWNLOAD_TRIGGER_STAGGER_MS = 500;
  // A still-queued download (e.g. dashboard 3 of 4 waiting on 1 and 2's own staggered turn) has
  // nothing left to fire into once this page is gone - without this check, one that hadn't had its
  // turn yet still calls triggerExportDownload later against an unmounted page (the real-world
  // equivalent of the test-isolation bug this exact guard was written to fix: a previous test's own
  // still-queued download firing mid-way through the *next* test, since the queue is a plain
  // promise chain with no other tie to component lifecycle).
  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );
  const queueDownloadTrigger = (response: ExportDownloadOut) => {
    downloadTriggerQueueRef.current = downloadTriggerQueueRef.current.then(
      () =>
        new Promise<void>((resolve) => {
          if (!isMountedRef.current) {
            resolve();
            return;
          }
          triggerExportDownload(response);
          setTimeout(resolve, DOWNLOAD_TRIGGER_STAGGER_MS);
        })
    );
  };

  const handleDownloadCsv = (dashboardId: string, overrideExportName?: string) => {
    const dashboard = dashboards.find((d) => d.key === dashboardId);
    if (!dashboard) return;
    // The last confirmed-applied values, not the live (possibly since-edited) form state -
    // matches what the backend itself last validated for this dashboard.
    const requestFilters = buildRequestFilters(
      dashboardId,
      filterFieldsByDashboard[dashboardId] ?? [],
      appliedFilterValuesByDashboard[dashboardId] ?? {}
    );
    const exportName = overrideExportName ?? exportNameByDashboard[dashboardId] ?? exportOptionsByDashboard[dashboardId]?.[0]?.value;
    // Set (even on the internal retry below - already true there, this is a no-op) rather than
    // only on the very first call, so a caller re-entering this function directly (the retry)
    // doesn't need its own bookkeeping. Only ever cleared in this function's two terminal
    // branches further down - never here, and never in the retry branch itself.
    setDownloadingByDashboard((prev) => ({ ...prev, [dashboardId]: true }));
    exportDashboardAnalysis(dashboard.id, requestFilters, {
      export: exportName,
      page: pageByDashboard[dashboardId] || undefined,
    }).then((response) => {
      if (isExportDownload(response)) {
        setDownloadingByDashboard((prev) => ({ ...prev, [dashboardId]: false }));
        queueDownloadTrigger(response);
        return;
      }
      const errorMessage = typeof (response as { error?: unknown })?.error === "string" ? (response as { error: string }).error : "";
      const options = findExportOptions(errorMessage);
      if (overrideExportName === undefined && options.length > 0) {
        setExportOptionsByDashboard((prev) => ({ ...prev, [dashboardId]: options.map((value) => ({ value, label: value })) }));
        setExportNameByDashboard((prev) => ({ ...prev, [dashboardId]: options[0] }));
        handleDownloadCsv(dashboardId, options[0]);
        return;
      }
      setDownloadingByDashboard((prev) => ({ ...prev, [dashboardId]: false }));
      toast.error(`Couldn't download ${dashboard.name} - please try again.`);
    });
  };

  // The button itself is disabled until allFiltersApplied is true (SelectDashboardTile.tsx), so in
  // practice every dashboard here already has confirmed-applied filters - this per-dashboard check
  // stays anyway as a defensive backstop against a dashboard's applied state flipping between the
  // button becoming enabled and actually being clicked (e.g. a filter edited in another tab/tile
  // right before the click).
  const handleDownloadAllCsvs = () => {
    selectedDashboards.forEach((dashboard) => {
      if (filtersAppliedByDashboard[dashboard.key]) handleDownloadCsv(dashboard.key);
    });
  };

  return (
    <CedarShellLayout>
      <ErrorBoundary fallback={<ErrorMsg class={"formError"} />}>
        <div className="analysis_page" data-test-id="analysis-page">
          <div className="analysis_page_header">
            <AnalyticsOutlinedIcon className="page_header_icon" />
            <div>
              <h1 className="analysis_page_title">Downloads</h1>
              <p className="analysis_page_subtitle">
                Select one or more dashboards, apply filters and download the data — individually or all at once.
              </p>
            </div>
          </div>

          <SelectDashboardTile
            dashboards={dashboards}
            selectedDashboardIds={selectedDashboardIds}
            onAdd={handleAddDashboard}
            onRemove={handleRemoveDashboard}
            onApplyAllFilters={handleApplyAllFilters}
            onDownloadAllCsvs={handleDownloadAllCsvs}
            allFiltersApplied={allFiltersApplied}
            allSelectedFiltersLoaded={allSelectedFiltersLoaded}
            hasAccessibleSelectedDashboard={hasAccessibleSelectedDashboard}
            isDownloadingAnyCsv={selectedDashboards.some((dashboard) => downloadingByDashboard[dashboard.key])}
          />

          {selectedDashboards.map((dashboard) => (
            <ApplyFiltersTile
              key={dashboard.key}
              dashboardId={dashboard.key}
              dashboardName={dashboard.name}
              filters={filterFieldsByDashboard[dashboard.key] ?? []}
              values={filterValuesByDashboard[dashboard.key] ?? {}}
              hasAppliedFilters={filtersAppliedByDashboard[dashboard.key] ?? false}
              isLoadingFilters={filtersLoadingByDashboard[dashboard.key] ?? false}
              filtersAccessDenied={filtersAccessDeniedByDashboard[dashboard.key] ?? false}
              exportOptions={exportOptionsByDashboard[dashboard.key]}
              exportName={exportNameByDashboard[dashboard.key]}
              needsPage={needsPageByDashboard[dashboard.key] ?? false}
              page={pageByDashboard[dashboard.key] ?? ""}
              pageOptions={pageOptionsByDashboard[dashboard.key]}
              onPageChange={(page) => handlePageChange(dashboard.key, page)}
              onExportNameChange={(name) => setExportNameByDashboard((prev) => ({ ...prev, [dashboard.key]: name }))}
              onChange={(key, value) => handleFilterChange(dashboard.key, key, value)}
              onApply={() => handleApplyFilters(dashboard.key)}
              onReset={() => handleResetFilters(dashboard.key)}
              onRemove={() => handleRemoveDashboard(dashboard.key)}
              onDownloadCsv={() => handleDownloadCsv(dashboard.key)}
              isDownloadingCsv={downloadingByDashboard[dashboard.key] ?? false}
              applyError={applyErrorByDashboard[dashboard.key]}
            />
          ))}
        </div>
      </ErrorBoundary>
    </CedarShellLayout>
  );
};
